import React, { useCallback } from 'react';
import { useFocusEffect, useRouter } from 'expo-router';
import { defineMessages, type FormatNumberOptions, useIntl, FormattedMessage } from 'react-intl';
import { Platform, Text } from 'react-native';

import {
  Button,
  FocusScope,
  TextSpace,
  useAppState,
  useKeyboardListener,
  useIsFocused,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sg/garnish';
import { type Product } from '@sg/graphql-schema';
import { BagRewardWarning } from '@sg/shared-fe-features-loyalty';

import { adjustWantedTime, Bag } from '@order/features-ordering';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { isTomorrow } from '@order/utils';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useNavigateToLastInteractedStore } from '../../../../context/LastInteractedStore';
import {
  useBagContent,
  useCloseBag,
  useIsUpsellScreenEnabled,
  useNavigateToMenu,
} from '../../../../hooks';
import { useBagDisclosure, type useOrderState, useUpsells } from '../../hooks';
import { BagLineItems } from '../BagLineItems';
import { BagLoyalty } from '../BagLoyalty';
import { CategoryUpsells } from '../CategoryUpsells';

export const BagContent = (props: BagContentProps) => {
  const { orderState } = props;

  const { formatMessage, formatNumber } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { minWidth } = useResponsive();

  // ─── Flags ────────────────────────────────────────────────────────────────

  const {
    isBagEmpty,
    isBagValid,
    isFetchingInitialData,
    isSubmittingOrder,
    isShowingFutureDateWarning,
    isShowingRewardWarning,
    isShowingTimeslotAdjustedNotice,
    isLineItemQuantityChangePending,
    isPerformingBagAction,
    isShowingWarning,
    isShowingModal,
    isPostOrder,
    isPollingPayment,
    isPaymentPending,
    isPaymentDelayed,
    isPaymentFulfilled,
    isShowingOrderPlayback,
    isCancelingPayment,
    isShowingUpsellInterstitial,
    isAddingUpsellFromInterstitial,
    areUpsellInteractionsDisabled,
  } = orderState;

  const isFocused = useIsFocused();

  const isFocusable = !isShowingModal && !isShowingWarning;

  const canContinueToCheckout =
    !isPerformingBagAction && !isLineItemQuantityChangePending && !isShowingModal;
  const isUpsellScreenEnabled = useIsUpsellScreenEnabled();
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  // ─── Context ──────────────────────────────────────────────────────────────

  const {
    bagTracking,
    wantedTime,
    previousWantedTime,
    dropoffLocation = '',
    lineItemIdsBeingUpdated,
    cart,
    rewardError,
    isRewardMissingSku,
    shouldIncludeUtensils,
    upsellItems,
    placementVariation,
  } = orderState;

  // ─── Cart ─────────────────────────────────────────────────────────────────

  const {
    orderId = '',
    orderChannel = 'pickup',
    locationName = '',
    restaurantId = '',
    restaurantSlug = '',
    dropoffLocations,
    addressId,
    isGroupOrder,
    lineItems,
    ledger,
    showDeliveryFeeDisclosure = false,
    showOutpostPriceDifferenciationDisclosure = false,
    showDeliveryPriceDifferenciationDisclosure = false,
    deliveryFee = 0,
    deliveryMinSubtotal = 0,
  } = cart;

  // ─── Actions ──────────────────────────────────────────────────────────────

  const {
    startPostRedirectDelay,
    addUpsell,
    isAddingUpsellToBag,
    startChangingLineItemQuantity,
    stopChangingLineItemQuantity,
    changeLineItemQuantity,
    cancelRequest,
    cancelPreviousIntent,
    requestChannelOrLocationChange,
    requestDeliveryPreferencesChange,
    requestTimeChange,
    requestOrderSubmission,
    changeDropoffLocation,
    changeUtensilsUsage,
    requestCheckoutLedger,
  } = orderState;

  // ─── Headers ──────────────────────────────────────────────────────────────

  const adjustedTime = adjustWantedTime(wantedTime);
  const isFutureDate = adjustedTime ? isTomorrow(adjustedTime) : false;
  const hasLongerLocationName = locationName.length > 24;
  const shouldShowGroupOrder = isGroupOrderingEnabled && isGroupOrder;

  // ─── Tracking ─────────────────────────────────────────────────────────────

  const {
    trackContinuePressed,
    trackContinueDeliveryMinimumBlocked,
    trackViewMenuPressed,
    trackLineItemPressed,
    trackUpsellModalContinuePressed,
    trackUpsellModalClosePressed,
  } = bagTracking;

  // ─── Navigation ───────────────────────────────────────────────────────────

  const router = useRouter();
  const closeBag = useCloseBag();

  const handleViewMenu = useNavigateToLastInteractedStore({ isInsideModal: true }); // prettier-ignore
  const navigateToMenu = useNavigateToMenu();
  const navigateToBagMenu = useCallback(() => {
    trackViewMenuPressed();
    navigateToMenu({
      orderType: orderChannel,
      restaurantSlug,
      deliveryOrderDetail: { addressId },
      referrer: 'bag',
    });
  }, [orderChannel, restaurantSlug, addressId, navigateToMenu, trackViewMenuPressed]);

  // ─── Delivery Minimum ─────────────────────────────────────────────────────

  const isDeliveryMinimumBlockEnabled = useFeatureFlag('CELS-5341-order-delivery-minimum-block-enabled'); // prettier-ignore
  const deliveryMinimum = orderChannel === 'delivery' ? deliveryMinSubtotal : 0;
  const deliveryAmountNeeded = deliveryMinimum - (ledger?.subtotal ?? 0);
  const isBelowDeliveryMinimum = isDeliveryMinimumBlockEnabled && deliveryMinimum > 0 && deliveryAmountNeeded > 0; // prettier-ignore

  // ─── Auth State ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();

  const handleContinue = useCallback(() => {
    trackContinuePressed();

    if (isBelowDeliveryMinimum) {
      const amountNeeded = formatNumber(deliveryAmountNeeded / 100, CURRENCY_FORMAT_OPTIONS);
      const minimumAmount = formatNumber(deliveryMinimum / 100, CURRENCY_FORMAT_OPTIONS);

      trackContinueDeliveryMinimumBlocked(minimumAmount, amountNeeded);

      addNoticeBanner({
        palette: 'neutral',
        text: formatMessage(messages.deliveryMinimumNotMet, {
          amount_needed: amountNeeded,
          minimum: minimumAmount,
        }),
      });

      return;
    }

    if (!isLoggedIn) {
      router.dismissTo({
        pathname: '/(auth)/join-or-sign-in',
        params: { redirect: '/bag' },
      });

      return;
    }

    // NOTE: Machine intercepts call to show upsell modal if eligible
    requestCheckoutLedger();
  }, [
    trackContinuePressed,
    trackContinueDeliveryMinimumBlocked,
    isBelowDeliveryMinimum,
    deliveryAmountNeeded,
    deliveryMinimum,
    addNoticeBanner,
    formatMessage,
    formatNumber,
    isLoggedIn,
    requestCheckoutLedger,
    router,
  ]);

  // ─── Contentful ───────────────────────────────────────────────────────────

  const {
    viewMenuCta,
    viewMenuA11y,
    continueToCheckoutCta,
    deliveryDisclosure,
    outpostDisclosure,
    timeslotUnavailableWarning,
    timeslotUnavailableWarningContinueCta,
    timeslotUnavailableWarningBackCta,
    loadingTitle,
    postRedirectTitle,
    postRedirectBody,
    postRedirectDelayedTitle,
    postRedirectDelayedBody,
    postRedirectDelayedCta,
  } = useBagContent();

  const disclosure = useBagDisclosure({
    orderChannel,
    showDeliveryFeeDisclosure,
    showOutpostPriceDifferenciationDisclosure,
    showDeliveryPriceDifferenciationDisclosure,
    deliveryFee,
    deliveryMinSubtotal,
    deliveryDisclosure,
    outpostDisclosure,
  });

  // ─── Upsells ──────────────────────────────────────────────────────────────

  const { categoryUpsells, isLoadingUpsells, shouldRenderCategoryUpsells, hideCategoryUpsells } =
    useUpsells({ orderId, restaurantId, orderChannel, lineItems });

  const addUpsellToBag = useCallback(
    (payload: { quantity?: number; product: Partial<Product> }) => {
      const { quantity, product } = payload;

      addUpsell({
        productId: product.id ?? '',
        productName: product.name ?? '',
        quantity: quantity ?? 1,
        placementVariation,
      });
    },
    [addUpsell, placementVariation],
  );

  // ─── Upsell Exp. Logic ───────────────────────────────────────────────────────────────────

  const isModalCohort = placementVariation === 'bag-to-checkout-modal';
  const isEligibleForExperiment = upsellItems.length > 0;

  const handleUpsellModalContinue = useCallback(() => {
    if (areUpsellInteractionsDisabled) {
      return;
    }

    trackUpsellModalContinuePressed();

    requestCheckoutLedger();
  }, [trackUpsellModalContinuePressed, requestCheckoutLedger, areUpsellInteractionsDisabled]);

  const handleUpsellModalClose = useCallback(() => {
    if (areUpsellInteractionsDisabled) {
      return;
    }

    trackUpsellModalClosePressed();

    cancelRequest();
  }, [trackUpsellModalClosePressed, cancelRequest, areUpsellInteractionsDisabled]);

  // ─── Loading ───────────────────────────────────────────────────────────────

  const isPostRedirectSubmission = isSubmittingOrder && !isShowingOrderPlayback;
  const isLoadingBag =
    isFetchingInitialData ||
    isPollingPayment ||
    isLoadingUpsells ||
    isPostOrder ||
    isPostRedirectSubmission;

  // ─── Floating Ctas ────────────────────────────────────────────────────────
  // When the keyboard is open on Android, the floating cta hides the input field.

  const { isKeyboardOpen } = useKeyboardListener();
  const shouldHideFloatingCtas = Platform.OS === 'android' && isKeyboardOpen;
  const shouldShowFloatingCtas = !shouldHideFloatingCtas && isBagValid && !isLoadingBag;

  // ─── Loading State ────────────────────────────────────────────────────────

  const isPostRedirect = isPaymentPending || isPaymentDelayed || isPaymentFulfilled;
  const loadingTitlePostRedirect = isPaymentDelayed ? postRedirectDelayedTitle : postRedirectTitle; // prettier-ignore
  const loadingBodyPostRedirect = isPaymentDelayed ? postRedirectDelayedBody : postRedirectBody; // prettier-ignore
  const loadingStateTitle = isPostRedirect ? loadingTitlePostRedirect : loadingTitle; // prettier-ignore
  const loadingStateBody = isPostRedirect ? loadingBodyPostRedirect : ''; // prettier-ignore
  const loadingStateCta = isPostRedirect && isPaymentDelayed ? postRedirectDelayedCta : null; // prettier-ignore

  const handleStartPostRedirectDelay = useCallback(() => {
    if (!isPostRedirect) return;
    startPostRedirectDelay();
  }, [isPostRedirect, startPostRedirectDelay]);

  useFocusEffect(handleStartPostRedirectDelay);
  useAppState({ onVisible: handleStartPostRedirectDelay });

  // ─── Upsell Rendering ──────────────────────────────────────────────────────

  const canRenderUpsells = !isUpsellScreenEnabled && !isLoadingBag && !isPostRedirect; // prettier-ignore

  if (shouldRenderCategoryUpsells && canRenderUpsells && placementVariation === 'control') {
    return (
      <FocusScope enabled={Boolean(isFocused && isFocusable)}>
        <CategoryUpsells
          categories={categoryUpsells}
          upsellIdsBeingAdded={lineItemIdsBeingUpdated}
          addUpsellToBag={addUpsellToBag}
          onClose={closeBag}
          onContinue={hideCategoryUpsells}
        />
      </FocusScope>
    );
  }

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <FocusScope enabled={Boolean(isFocused && isFocusable)}>
      {/* Loading state for the bag. */}
      {isLoadingBag ? (
        <Bag.LoadingState
          title={loadingStateTitle}
          body={loadingStateBody}
          cta={loadingStateCta}
          isLoading={isCancelingPayment}
          onPress={cancelPreviousIntent}
        />
      ) : null}

      {/* Empty state for the bag. */}
      {isBagEmpty && !isLoadingBag ? <Bag.EmptyState onViewMenu={handleViewMenu} /> : null}

      {/* Loading state overlay for when actions are performed in the bag, not needed for initial fetching or placing orders. */}
      <Bag.LoadingOpacity
        isLoading={Boolean(isPerformingBagAction && !isLoadingBag && !isSubmittingOrder)}
      />

      <Bag.WarningModal visible={isShowingWarning} onClose={cancelRequest}>
        {/* Warning that the selected date is in the future. */}
        {isShowingFutureDateWarning ? <Bag.FutureDateWarning onClose={cancelRequest} /> : null}

        {/* Warning that the reward was not applied. */}
        {isShowingRewardWarning ? (
          <BagRewardWarning
            message={rewardError}
            isRewardMissingSku={isRewardMissingSku}
            onViewMenu={handleViewMenu}
            onClose={cancelRequest}
          />
        ) : null}

        {/* Warning that the wanted time was adjusted. */}
        {isShowingTimeslotAdjustedNotice ? (
          <Bag.TimeslotUnavailableWarning
            wantedTime={wantedTime}
            previousWantedTime={previousWantedTime}
            message={timeslotUnavailableWarning}
            continueCta={timeslotUnavailableWarningContinueCta}
            backCta={timeslotUnavailableWarningBackCta}
            onContinue={requestCheckoutLedger}
            onBack={requestTimeChange}
          />
        ) : null}
      </Bag.WarningModal>

      {/* Actual bag contents. */}
      {isBagValid && !isLoadingBag ? (
        <Bag.ScrollView
          orderChannel={orderChannel}
          locationName={locationName}
          isGroupOrder={shouldShowGroupOrder}
          viewMenuCta={viewMenuCta}
          viewMenuA11y={viewMenuA11y}
          isDisabled={isPerformingBagAction}
          automaticallyAdjustKeyboardInsets={!isShowingModal}
          onViewMenu={navigateToBagMenu}
          requestChannelOrLocationChange={requestChannelOrLocationChange}
          requestDeliveryPreferencesChange={requestDeliveryPreferencesChange}
          onHeaderCloseBtnPress={closeBag}
        >
          <Bag.ContentContainer>
            {/* Header with channel, location, time and dropoff location. */}
            <Bag.HeaderContainer>
              <Bag.HeaderLine sizeMatch={[hasLongerLocationName ? '24' : '32']} numberOfLines={3}>
                <Bag.ChannelHeader
                  orderChannel={orderChannel}
                  requestChannelOrLocationChange={requestChannelOrLocationChange}
                />

                <TextSpace />

                <Bag.TimeConnector time={wantedTime} />

                <TextSpace />

                <Bag.TimeHeader time={wantedTime} requestTimeChange={requestTimeChange} />

                <TextSpace />

                {isFutureDate ? null : <Text>{'\n'}</Text>}

                {orderChannel === 'pickup' ? <Bag.LocationFromConnector /> : null}

                {orderChannel === 'delivery' ? <Bag.LocationToConnector /> : null}

                {orderChannel === 'outpost' ? <Bag.LocationAtConnector /> : null}

                <TextSpace />

                <Bag.LocationHeader
                  orderChannel={orderChannel}
                  locationName={locationName}
                  requestChannelOrLocationChange={requestChannelOrLocationChange}
                  requestDeliveryPreferencesChange={requestDeliveryPreferencesChange}
                />
              </Bag.HeaderLine>
            </Bag.HeaderContainer>

            {orderChannel !== 'delivery' && dropoffLocations.length >= 2 ? (
              <Bag.DropoffLocationPicker
                selectedDropoffLocationId={dropoffLocation}
                firstDropoffLocation={dropoffLocations[0]}
                secondDropoffLocation={dropoffLocations[1]}
                changeDropoffLocation={changeDropoffLocation}
              />
            ) : null}

            {disclosure ? <Bag.Disclosure disclosure={disclosure} /> : null}

            {/* List of line items, able to edit quantity or change name. */}
            <BagLineItems
              isGroupOrder={isGroupOrder}
              lineItems={lineItems}
              lineItemIdsBeingUpdated={lineItemIdsBeingUpdated}
              restaurantSlug={restaurantSlug}
              addressId={addressId}
              isDelivery={orderChannel === 'delivery'}
              isDisabled={isPerformingBagAction}
              trackLineItemPressed={trackLineItemPressed}
              startChangingLineItemQuantity={startChangingLineItemQuantity}
              stopChangingLineItemQuantity={stopChangingLineItemQuantity}
              changeLineItemQuantity={changeLineItemQuantity}
            />

            {isEligibleForExperiment && placementVariation === 'bag-rail-above' ? (
              <Bag.Upsell.Rail
                upsellProducts={upsellItems}
                upsellIdsBeingAdded={lineItemIdsBeingUpdated}
                addUpsellToBag={addUpsellToBag}
                isAddingUpsellToBag={isAddingUpsellToBag}
                startChangingLineItemQuantity={startChangingLineItemQuantity}
                stopChangingLineItemQuantity={stopChangingLineItemQuantity}
                isDisabled={isPerformingBagAction}
              />
            ) : null}

            {/* Rewards rail and add promo code field. */}
            <BagLoyalty orderState={orderState} />

            {isEligibleForExperiment && placementVariation === 'bag-rail-below' ? (
              <Bag.Upsell.Rail
                upsellProducts={upsellItems}
                upsellIdsBeingAdded={lineItemIdsBeingUpdated}
                addUpsellToBag={addUpsellToBag}
                isAddingUpsellToBag={isAddingUpsellToBag}
                startChangingLineItemQuantity={startChangingLineItemQuantity}
                stopChangingLineItemQuantity={stopChangingLineItemQuantity}
                isDisabled={isPerformingBagAction}
              />
            ) : null}

            {orderChannel === 'delivery' ? (
              <Bag.UtensilsCta
                shouldIncludeUtensils={shouldIncludeUtensils}
                isDisabled={isPerformingBagAction}
                onChange={changeUtensilsUsage}
              />
            ) : null}

            <Bag.Footer isDisabled={isPerformingBagAction}>
              {ledger?.subtotal ? <Bag.Subtotal subtotal={ledger.subtotal} /> : null}

              {ledger?.discounts[0] ? <Bag.Discount {...ledger.discounts[0]} /> : null}
            </Bag.Footer>
          </Bag.ContentContainer>
        </Bag.ScrollView>
      ) : null}

      {shouldShowFloatingCtas ? (
        <Bag.FloatingCtas>
          <Bag.ContinueToCheckoutCta
            cta={continueToCheckoutCta}
            isDisabled={!canContinueToCheckout}
            onPress={handleContinue}
          />
        </Bag.FloatingCtas>
      ) : null}

      {isModalCohort && minWidth.isSM ? (
        <Bag.Upsell.Modal
          isModalVisible={isShowingUpsellInterstitial}
          onCancel={handleUpsellModalClose}
        >
          <Bag.Upsell.Grid
            upsellProducts={upsellItems}
            upsellIdsBeingAdded={lineItemIdsBeingUpdated}
            addUpsellToBag={addUpsellToBag}
            startChangingLineItemQuantity={startChangingLineItemQuantity}
            stopChangingLineItemQuantity={stopChangingLineItemQuantity}
            isAddingUpsellToBag={isAddingUpsellFromInterstitial}
            isContinueDisabled={areUpsellInteractionsDisabled}
            isDisabled={isPerformingBagAction}
            onContinue={handleUpsellModalContinue}
          />
        </Bag.Upsell.Modal>
      ) : null}

      {isModalCohort && !minWidth.isSM ? (
        <Bag.Upsell.Modal
          isModalVisible={isShowingUpsellInterstitial}
          onCancel={handleUpsellModalClose}
        >
          <Bag.Upsell.Rail
            upsellProducts={upsellItems}
            upsellIdsBeingAdded={lineItemIdsBeingUpdated}
            addUpsellToBag={addUpsellToBag}
            startChangingLineItemQuantity={startChangingLineItemQuantity}
            stopChangingLineItemQuantity={stopChangingLineItemQuantity}
            isAddingUpsellToBag={isAddingUpsellFromInterstitial}
            isDisabled={isPerformingBagAction}
            withoutHeader
            withoutBorder
          />

          <Button
            size="large-wide"
            onPress={handleUpsellModalContinue}
            disabled={areUpsellInteractionsDisabled}
          >
            <FormattedMessage {...messages.continueCta} />
          </Button>
        </Bag.Upsell.Modal>
      ) : null}
    </FocusScope>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryMinimumNotMet: {
    defaultMessage:
      'Delivery minimum not met. Add {amount_needed} more to meet the {minimum} delivery minimum.',
    description: 'Bag | Continue to checkout | Delivery minimum not met',
  },
  continueCta: {
    defaultMessage: 'Continue',
    description: 'Bag | Upsell | Modal | Continue CTA Label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CURRENCY_FORMAT_OPTIONS: FormatNumberOptions = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagContentProps = {
  orderState: ReturnType<typeof useOrderState>;
};
